import {
  RebateBookingsOverview,
  RebateBookingsReservedBooking,
} from "../types/types";
import { formatPeriod, formatRebateTerm } from "./formatter";
import { getActiveGoal, getCriteriaNames } from "./rebateOverviewUtils";

export interface RouteReservedBookings {
  routeCode: string;
  bookings: RebateBookingsReservedBooking[];
  totalReserved: number;
}

/** Shown for bookings the API returns without a route code. */
export const UNKNOWN_ROUTE_CODE = "-";

export const groupReservedBookingsByRoute = (
  rebate: RebateBookingsOverview | undefined
): RouteReservedBookings[] => {
  const byRouteCode = new Map<string, RebateBookingsReservedBooking[]>();

  for (const booking of rebate?.reservedBookings ?? []) {
    const routeCode = booking.routeCode ?? UNKNOWN_ROUTE_CODE;
    const bookings = byRouteCode.get(routeCode);

    if (bookings) {
      bookings.push(booking);
    } else {
      byRouteCode.set(routeCode, [booking]);
    }
  }

  return Array.from(byRouteCode.entries())
    .map(([routeCode, bookings]) => ({
      routeCode,
      bookings,
      totalReserved: bookings.reduce(
        (sum, booking) => sum + booking.rebateAmountReserved,
        0
      ),
    }))
    .sort((a, b) => a.routeCode.localeCompare(b.routeCode));
};

/**
 * The Excel export flattens every route table into one sheet, so the rows are
 * ordered the way the expanded route cards read top to bottom.
 */
export const getReservedBookingsForExport = (
  rebate: RebateBookingsOverview | undefined
): RebateBookingsReservedBooking[] =>
  groupReservedBookingsByRoute(rebate).flatMap((route) =>
    [...route.bookings].sort((a, b) =>
      a.departureDate.localeCompare(b.departureDate)
    )
  );

const FALLBACK_EXPORT_FILENAME = "rebate-bookings";

/** Characters Windows and macOS refuse in a file name. */
const illegalFilenameCharacters = /[\\/:*?"<>|]/g;

/**
 * Names the exported file after the header card: agreement name, rebate term
 * and period.
 */
export const createRebateBookingsExportFilename = (
  rebate: RebateBookingsOverview | undefined
) => {
  if (!rebate) {
    return FALLBACK_EXPORT_FILENAME;
  }

  const filename = [
    rebate.rebateAgreement?.name,
    formatRebateTerm(rebate.rebateTermCode),
    formatPeriod(
      rebate.period,
      rebate.rebateTermCode,
      getActiveGoal(rebate)?.validFrom
    ),
  ]
    .filter(Boolean)
    .join(" - ")
    .replace(illegalFilenameCharacters, " ")
    .replace(/\s+/g, " ")
    .trim();

  return filename || FALLBACK_EXPORT_FILENAME;
};

const escapeHtml = (value: string) =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/**
 * The clipboard header, as rows for the copied table: agreement name and term
 * on the first, criteria and period on the second, mirroring the summary card
 * above the route cards.
 *
 * They are rows rather than markup before the table because a spreadsheet
 * pasting HTML keeps what is inside the table and may drop anything around it.
 */
export const createRebateBookingsHeaderRows = (
  rebate: RebateBookingsOverview,
  columnCount: number
): string => {
  const toRow = (cells: string[]) => {
    const padded = [
      ...cells,
      ...Array(Math.max(0, columnCount - cells.length)).fill(""),
    ];

    return `<tr>${padded.map((cell) => `<td>${cell}</td>`).join("")}</tr>`;
  };

  const name = rebate.rebateAgreement?.name ?? "";
  const period = formatPeriod(
    rebate.period,
    rebate.rebateTermCode,
    getActiveGoal(rebate)?.validFrom
  );

  return (
    toRow([
      `<b>${escapeHtml(name)}</b>`,
      escapeHtml(formatRebateTerm(rebate.rebateTermCode)),
    ]) + toRow([escapeHtml(getCriteriaNames(rebate)), escapeHtml(period)])
  );
};
